// Wire shapes and pure helpers for the staff organisations page. Like the customer /me read,
// the admin responses carry anonymous shapes the generated schema does not type, so the wire
// types live here by hand.

export type OrgSummary = { id: string; name: string; slug: string | null };

export type AdminGrant = { kind: string; ref: string; basis: string; expiresAt?: string | null };

export type AdminProduct = {
  status: 'active' | 'available' | 'hidden';
  trialAvailable?: boolean;
  roleBasedAccess?: boolean;
  grants?: AdminGrant[];
};

export type AdminProducts = Partial<Record<string, AdminProduct>>;

export function filterOrgs(orgs: OrgSummary[], query: string): OrgSummary[] {
  const q = query.trim().toLowerCase();
  if (!q) return orgs;
  return orgs.filter((o) => o.name.toLowerCase().includes(q) || o.id.toLowerCase().includes(q));
}

// The grant route's item segment — the same "<kind>:<slug>" form the catalogue uses.
export function grantItemId(grant: { kind: string; ref: string }): string {
  return `${grant.kind}:${grant.ref}`;
}

export function capitalise(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

// Humanises a "<kind>:<slug>" item id for display: the slug's words lead, the kind trails
// ("tier:advanced" → "Advanced tier"). The raw id stays visible elsewhere for copy-paste;
// this is the name a person reads.
export function itemLabel(itemId: string): string {
  const colon = itemId.indexOf(':');
  if (colon < 0) return itemId;
  const kind = itemId.slice(0, colon);
  const words = itemId.slice(colon + 1).split('-').filter(Boolean).map(capitalise).join(' ');
  return kind === 'tier' || kind === 'module' ? `${words} ${kind}` : words;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A date-only expiry means "through that day": local end of day, not the UTC midnight that
// new Date('YYYY-MM-DD') gives — which both cut paid access a day short and told operators
// west of Greenwich that tomorrow was "in the past". Empty → null; unparseable → invalid Date.
export function parseExpiry(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (DATE_ONLY.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    const parsed = new Date(y, m - 1, d, 23, 59, 59, 999);
    // Reject rollover (2026-13-45 would otherwise silently become a real date).
    return parsed.getMonth() === m - 1 && parsed.getDate() === d ? parsed : new Date(Number.NaN);
  }
  return new Date(trimmed);
}

// An optional expiry: empty is fine (perpetual); otherwise it must parse and be in the future,
// mirroring the endpoint's expires_at_in_past rejection so staff hear it before the round-trip.
export function expiryError(value: string, now: Date): string | null {
  const parsed = parseExpiry(value);
  if (parsed === null) return null;
  if (Number.isNaN(parsed.getTime())) return 'Enter a date like 2026-12-31, or leave empty.';
  if (parsed.getTime() <= now.getTime()) return 'Expiry must be in the future.';
  return null;
}

// The wire value for a grant write; undefined when empty or unparseable (validation runs first).
export function expiryToIso(value: string): string | undefined {
  const parsed = parseExpiry(value);
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.toISOString() : undefined;
}

// Table rendering for a grant's expiry: lapsed grants are retained by the backend (they mark a
// trial as taken), so the admin view must say so rather than show a bare past date.
export function expiryDisplay(expiresAt: string | null | undefined, now: Date): { label: string; lapsed: boolean } {
  if (!expiresAt) return { label: '—', lapsed: false };
  const time = new Date(expiresAt).getTime();
  if (Number.isNaN(time)) return { label: '—', lapsed: false };
  const date = new Date(time).toLocaleDateString();
  return time <= now.getTime() ? { label: `Expired ${date}`, lapsed: true } : { label: date, lapsed: false };
}

// Same liveness rule as the customer read (src/api/me.ts), except an unparseable expiry counts
// as live here: when in doubt, don't offer a re-grant that would silently replace it.
export function isLiveGrant(grant: Pick<AdminGrant, 'expiresAt'>, now: Date): boolean {
  if (!grant.expiresAt) return true;
  const time = new Date(grant.expiresAt).getTime();
  return Number.isNaN(time) || time > now.getTime();
}

// What the picker may offer: the catalogue's grantable items minus anything the org holds live.
// A lapsed grant stays re-offerable; a live one must be revoked first, so a trial cannot be
// silently rewritten into a perpetual paid grant.
export function offerableItemIds(
  grantable: readonly string[], grants: readonly AdminGrant[], now: Date,
): string[] {
  const held = new Set(grants.filter((g) => isLiveGrant(g, now)).map(grantItemId));
  return grantable.filter((id) => !held.has(id));
}

// React row key: an org can hold two grants for the same item (a lapsed trial marks the trial
// as taken alongside the paid grant that replaced it), so the item id alone collides.
export function grantKey(grant: AdminGrant): string {
  return `${grantItemId(grant)}|${grant.basis}|${grant.expiresAt ?? ''}`;
}

// The assignable items for a product: current tiers and modules (never trial offers), as
// "<kind>:<slug>" ids — which is exactly the catalogue item id.
export function grantableItemIds(items: readonly { id: string; type: string }[]): string[] {
  return items.filter((i) => i.type === 'tier' || i.type === 'module').map((i) => i.id);
}

export type SsoCapabilityView = { enabled: boolean; since: string; reason: string; by: string };
export type AdminCapabilities = { sso: SsoCapabilityView | null; hasLivePaidGrant: boolean };

export const NO_CAPABILITIES: AdminCapabilities = { sso: null, hasLivePaidGrant: false };

// The API's closed list; anything else is shown raw so a new reason is never hidden.
const REASON_LABELS: Record<string, string> = {
  'first-paid-grant': 'first paid grant',
  staff: 'staff',
  lapsed: 'lapsed subscription',
};

// Node's ICU data renders 'short' September as "Sept", not "Sep" — spell the months out by
// hand so the format is stable across environments instead of relying on the locale's whim.
const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function describeSsoCapability(sso: SsoCapabilityView | null): string {
  if (!sso) return 'Not enabled';
  const when = new Date(sso.since);
  const date = `${when.getUTCDate()} ${SHORT_MONTHS[when.getUTCMonth()]} ${when.getUTCFullYear()}`;
  return `${sso.enabled ? 'Enabled' : 'Disabled'} since ${date} (${REASON_LABELS[sso.reason] ?? sso.reason})`;
}
